import React, {useMemo, useState} from "react";
import {Navigate, NavLink, Outlet, useLocation, useParams, useSearchParams} from "react-router-dom";
import {BookOpenIcon} from "lucide-react";
import SectionHeading from "../../../../components/SectionHeading";
import HelpSidebar from "./components/HelpSidebar";

type StepKey =
    "details"
    | "event-days"
    | "images"
    | "seatmap"
    | "tickets"
    | "data-collection"
    | "coupons"
    | "promoters"
    | "refund-policy"
    | "mailing"
    | "reminders"
    | "sponsors"
    | "speakers"
    | "feedback"
    | "options"
    | "review";

const STEPS: Array<{ key: StepKey; label: string; to: string }> = [
    {key: "details", label: "Event Details", to: "details"},
    {key: "event-days", label: "Event Days", to: "event-days"},
    {key: "images", label: "Images", to: "images"},
    {key: "seatmap", label: "Seat Map", to: "seatmap"},
    {key: "tickets", label: "Tickets", to: "tickets"},
    {key: "data-collection", label: "Data Collection", to: "data-collection"},
    {key: "coupons", label: "Coupons", to: "coupons"},
    {key: "promoters", label: "Promoters", to: "promoters"},
    {key: "refund-policy", label: "Refund Policy", to: "refund-policy"},
    {key: "mailing", label: "Mailing", to: "mailing"},
    {key: "reminders", label: "Reminders", to: "reminders"},
    {key: "sponsors", label: "Sponsors", to: "sponsors"},
    {key: "speakers", label: "Speakers & Line-up", to: "speakers"},
    {key: "feedback", label: "Feedback", to: "feedback"},
    {key: "options", label: "Options", to: "options"},
    {key: "review", label: "Review & Publish", to: "review"},
];

const GROUPS: Array<{ key: string; label: string; steps: StepKey[] }> = [
    {key: "basics", label: "Basics", steps: ["details", "event-days", "images", "seatmap"]},
    {key: "tickets", label: "Tickets", steps: ["tickets", "data-collection"]},
    {key: "promo", label: "Promotion", steps: ["coupons", "promoters", "sponsors", "speakers"]},
    {key: "comms", label: "Communications", steps: ["mailing", "reminders", "feedback"]},
    {key: "settings", label: "Settings", steps: ["refund-policy", "options"]},
    {key: "review", label: "Review", steps: ["review"]},
];

const stepByKey: Record<StepKey, { key: StepKey; label: string; to: string }> =
    Object.fromEntries(STEPS.map((s) => [s.key, s])) as Record<StepKey, { key: StepKey; label: string; to: string }>;

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

function getCurrentStep(pathname: string): StepKey {
    const seg = pathname.split("/").filter(Boolean).slice(-1)[0] as StepKey;
    return (seg ?? "details") as StepKey;
}

export default function ManageEventLayout() {
    const location = useLocation();
    const params = useParams();
    const [sp] = useSearchParams();

    const isEdit = location.pathname.includes("/edit");
    const eventId = isEdit ? (params.eventId ?? null) : sp.get("eventId");

    const step = useMemo(() => getCurrentStep(location.pathname), [location.pathname]);

    const [helpOpen, setHelpOpen] = useState(false);

    // guard: in create flow you must do details first
    if (!eventId && !isEdit && step !== "details") {
        return <Navigate to="/business/events/new/details" replace/>;
    }

    const basePath = isEdit
        ? `/business/events/${encodeURIComponent(eventId ?? "")}/edit`
        : `/business/events/new`;

    const activeGroup = GROUPS.find((g) => g.steps.includes(step)) ?? GROUPS[0];

    const stepTo = (to: string) =>
        isEdit
            ? `${basePath}/${to}`
            : {
                pathname: `${basePath}/${to}`,
                search: eventId ? `?eventId=${encodeURIComponent(eventId)}` : "",
            };

    return (
        <div className="space-y-6">

            <div className="flex items-start justify-between gap-4">
                <SectionHeading
                    title={isEdit ? "Edit Event" : "Create Event"}
                    description="Set up your event details, images, tickets, and more."
                />
                <button
                    type="button"
                    onClick={() => setHelpOpen((o) => !o)}
                    aria-label={helpOpen ? "Hide help" : "Show help"}
                    aria-expanded={helpOpen}
                    className="mt-1 hidden shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-300 hover:text-gray-900 xl:inline-flex"
                >
                    <BookOpenIcon className="size-4"/>
                    {helpOpen ? "Hide help" : "Help"}
                </button>
            </div>

            <div className="mt-10 space-y-3">
                {/* Row 1: group tabs */}
                <div className="border-b border-gray-200">
                    <nav
                        className="-mb-px flex gap-4 overflow-x-auto scrollbar-hide sm:gap-6"
                        aria-label="Sections"
                    >
                        {GROUPS.map((g) => {
                            const groupDisabled = !eventId && !isEdit && !g.steps.includes("details");
                            const isActive = g.key === activeGroup.key;

                            return (
                                <NavLink
                                    key={g.key}
                                    to={stepTo(stepByKey[g.steps[0]].to) as any}
                                    onClick={(e) => {
                                        if (groupDisabled) e.preventDefault();
                                    }}
                                    className={cx(
                                        "shrink-0 whitespace-nowrap border-b-2 pb-3",
                                        "px-2 text-sm font-semibold sm:px-1",
                                        groupDisabled
                                            ? "cursor-not-allowed border-transparent text-gray-300"
                                            : isActive
                                                ? "border-orange-600 text-orange-700"
                                                : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                                    )}
                                >
                                    {g.label}
                                </NavLink>
                            );
                        })}
                    </nav>
                </div>

                {/* Row 2: sub-tabs for the active group */}
                {activeGroup.steps.length > 1 && (
                    <nav
                        className="flex flex-wrap gap-2"
                        aria-label={`${activeGroup.label} steps`}
                    >
                        {activeGroup.steps.map((key) => {
                            const s = stepByKey[key];
                            const disabled = !eventId && !isEdit && s.key !== "details";

                            return (
                                <NavLink
                                    key={s.key}
                                    to={stepTo(s.to) as any}
                                    onClick={(e) => {
                                        if (disabled) e.preventDefault();
                                    }}
                                    className={({isActive}) =>
                                        cx(
                                            "rounded-full px-3 py-1 text-xs font-medium sm:text-sm",
                                            disabled
                                                ? "cursor-not-allowed text-gray-300"
                                                : isActive
                                                    ? "bg-orange-50 text-orange-700"
                                                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                        )
                                    }
                                >
                                    {s.label}
                                </NavLink>
                            );
                        })}
                    </nav>
                )}
            </div>

            <div className="flex gap-6 items-start">
                <div className="flex-1 min-w-0">
                    <Outlet context={{eventId, isEdit}}/>
                </div>
                {helpOpen && (
                    <div className="hidden xl:block w-72 shrink-0">
                        <HelpSidebar step={step}/>
                    </div>
                )}
            </div>
        </div>
    );
}
